import _ from 'lodash';
import Immutable from 'seamless-immutable';
import RefData from 'ref-data';

import { getAssetRefWithFallback, replaceIntegrationGenericFields, getIntegrationGenericAssetRef } from './utils';
import IntegrationEnums from './integration-enums';

/**
 * Integration
 * @typedef {object} Integration
 * @property {object} blueprint - describes the fields required to configure the integration
 * @property {refData} classRef - class of the integration
 * @property {string} containerRef - the container to which this integration belongs
 * @property {refData} eventType - the event that triggers the integration
 * @property {object} fieldMapping - a complex object that links the properties of a form field to the variable value it points to
 * @property {object} mappingData - a complex object used only on the FE that is a grouped up set of data based on fieldMapping from the current integration instance; currently supports 3 keys of formData, experienceMetaData and otherData, check _deserializeMappingData for more details
 * @property {string} help - help text to assist users in filling in the integration config field
 * @property {string} icon - path to the icon to use for the integration in the list
 * @property {string} id
 * @property {object} instanceConfig - the integration config that a user has saved in the config fields
 * @property {string} instanceConfigText - the string equivalent for instanceConfig passed down by the BE and used in the integrationDesigner; the integration designer is responsible for making proper use of this value
 * @property {string} lastUpdated
 * @property {object} modifiedBy
 * @property {string} name name of the integration
 * @property {refData} status - status of the integration
 * @property {object} typeConfig
 * @property {refData} typeRef - the type of integration

/**
 * Create a IntegrationModel from provided data
 */

function create(integration = {}) {
  const result = _.clone(integration);
  result.classRef = _.isString(integration.classRef)
    ? RefData.Model.hydrate(integration.classRef)
    : integration.classRef;
  result.eventType = _.isString(integration.eventType)
    ? RefData.Model.hydrate(integration.eventType)
    : integration.eventType;
  result.status = _.isString(integration.status) ? RefData.Model.hydrate(integration.status) : integration.status;
  result.typeRef = _.isString(integration.typeRef) ? RefData.Model.hydrate(integration.typeRef) : integration.typeRef;

  return Immutable(result, { prototype: model });
}

function fieldTypeMap(fieldType) {
  switch (fieldType) {
    case IntegrationEnums.integrationFieldTypes.SCRIPT:
      return IntegrationEnums.integrationFieldTypes.TEXTAREA;
    case IntegrationEnums.integrationFieldTypes.REMOTE_FIELDS:
      return IntegrationEnums.integrationFieldTypes.MULTISELECT;
    case IntegrationEnums.integrationFieldTypes.SECRET:
      return IntegrationEnums.integrationFieldTypes.PASSWORD;
  }
  return fieldType;
}

/**
 *  A method used to parse the mappingData received from the BE to create an easier to use object in the Integration Designer
 * @param {Object} integration - current integration instance data
 * @returns {Object} - creates and returns mappingData filled for the FE that is derived by grouping up mappingData from the integration instance in question. This value is to only be used on the FE side and it is currently only used in the IntegrationDesigner.
 */
function _deserializeMappingData(integration) {
  const mappingData = {
    [IntegrationEnums.integrationDesignerMappingData.FORM_DATA]:            [],
    [IntegrationEnums.integrationDesignerMappingData.EXPERIENCE_META_DATA]: [],
    [IntegrationEnums.integrationDesignerMappingData.OTHER_DATA]:           [],
  };

  //Local helper method that tries to look for mapping data before it gets inserted in the group to make sure that it is a unique entry for that group
  const _updateMappingData = (mappingData, key, value, group) => {
    //We want to add the mapping data to a specific group ONLY if the instance of it is unique. Each data is mapped by its key, so we will check if the key is unique, if it is, we push it to the list, else we skip as it is already present
    //Some data can have the same key multiple times (if "ngx_key" is set as string target) ... for example "params.ngx_key" and "params.ngx_key_type"
    const existing = mappingData[group].find((dataItem) => Object.keys(dataItem)[0] === key);
    if (!existing) mappingData[group].push({ [key]: value });
  };

  if (!_.isEmpty(integration.fieldMapping)) {
    //on some poorly configured integrations (mostly the very old ones) this value can be null or undefined and we need to work with that in mind
    for (const [key, value] of Object.entries(integration.fieldMapping)) {
      value.fields.forEach((field) => {
        //the group to which the mapped data will belong to ...keep in mind that each mapped data has more than one mapped param,which means it can end up in more than one group , but each group needs to have ONLY unique entries of said mapped data
        let _group = '';

        //deciding which group it belongs to
        switch (true) {
          case field.indexOf('params.') !== -1:
            //Parsing out the `params.` string part for each individual field. This process only has to happen in here as we need to cut out the `params.` part of the variable to avoid client confusion
            value.fields = value.fields.map((f) => f.replace(/^params\./, ''));
            _group = IntegrationEnums.integrationDesignerMappingData.FORM_DATA;
            break;

          case field.indexOf('experienceMetadata.') !== -1:
            _group = IntegrationEnums.integrationDesignerMappingData.EXPERIENCE_META_DATA;
            break;

          case ['entry.', 'campaign.', 'account.'].some((v) => field.includes(v)):
            _group = IntegrationEnums.integrationDesignerMappingData.OTHER_DATA;
            break;
        }

        //if we find a group that the element can belong to, we will then try to push it to the group
        if (_group) _updateMappingData(mappingData, key, value, _group); //this method will check if the entry is unique and will only added if no duplicates are part of the selected group
      });
    }
  }

  return mappingData;
}

/**
 * Transforms a backend integration object into a frontend IntegrationModel
 * @param {Object} integration - backend formatted integration
 * @returns {Integration}
 */
function deserialize(integration) {
  let result = _.pick(integration, _keys);

  result.blueprint = !_.isEmpty(result.blueprint) ? result.blueprint : { fields: [] };
  result.instanceConfig = _deserializeInstanceConfig(result);
  result.mappingData = _deserializeMappingData(result);

  // Hot data replacement for some kinds of integrations
  result = replaceIntegrationGenericFields(result);

  //We create as normal the full integration instance as an Immutable. This returns a proto-object where we have access to specific methods such as `set`,`setIn`,`merge`,`update`, etc. These methods will overwrite any existing matching keywords with their own; For example if we have a key in an object called `upgrade`, it will be replaced with the `update` method from Seamless-Immutable. This process also happens at a deep-level, were all inner objects will be transformed in this proto-object, replacing those specific keywords.
  const _createdIntegration = create(result);

  //WYN-6592 [Victor] We have encountered a problem where clients can dynamically build objects with any keyword, including keywords. This particular point of access for the clients is stored in the integration.instanceConfig and during the normal Immutable transformation we strip the object of specific keywords, like `set`,`setIn`,`merge`,`update`, etc. This is a problem as we have integration where the keyword `update` is needed. To work around the problem, we use the STATIC feature of Seamless-Immutable which will set the object as Immutable but it will not add any of the functional methods (such as `set`,`setIn`,`merge`,`update`, etc). We do this only for the `instanceConfig` key and merge it back into the Feature-Full Immutable object as to not impact the overall redux process.
  return _createdIntegration.set('instanceConfig', Immutable.static(integration.instanceConfig));
}

/**
 * Transform a IntegrationModel into a backend integration object
 * @returns {Object}
 */
function serialize(integration) {
  const result = integration.asMutable({ deep: true });

  result.classRef = integration.classRef.id;
  result.eventType = integration.eventType.id;
  result.status = integration.status.id;
  result.typeRef = integration.typeRef.id;

  //the point where we decide to pass the raw instanceConfigText or we parse the instanceConfig object into a string to pass to the BE , based on weather the integration is integration designer compatible or not
  result.instanceConfig = _serializeInstanceConfig(result);

  //we remove the intaceConfigText as it is a value used on the FE only to differ between editors and help ease in data submission to the BE
  delete result.instanceConfigText;

  //we do not want to submit the fieldMapping or mappingData to the BE
  delete result.fieldMapping;
  delete result.mappingData;

  return _.pick(result, _keys);
}

/** The IntegrationModel prototype */
const model = {
  get isDisabled() {
    return this.status.id === RefData.Enum.STATUS.INTEGRATION.INACTIVE;
  },

  get statusLabel() {
    return this.status.name;
  },

  get paramsType() {
    return this.instanceConfig.paramsType;
  },

  get assetRef() {
    return getIntegrationGenericAssetRef(
      this.typeRef.id,
      getAssetRefWithFallback({
        name:             this.name,
        icon:             this.icon,
        height:           100,
        width:            120,
        fallbackFontSize: '72px',
      })
    );
  },

  get supportsRemoval() {
    return ![RefData.Enum.TYPE.INTEGRATION.SHOPIFYEXTDATA, RefData.Enum.TYPE.INTEGRATION.ADOBECOMMERCEEXTDATA].includes(
      this.typeRef.id
    );
  },

  get supportEnableDisable() {
    //leaving it as an array for when ever we will add more than one integration types in here
    return ![RefData.Enum.TYPE.INTEGRATION.ADOBECOMMERCEEXTDATA].includes(this.typeRef.id);
  },
};

/**
 * Transforms base on field type: App -> API
 *
 * Certain fields can't be stored in the datastore in the form they need to be
 * presented to the user. Convert from the APP to DATASTORE form.
 *
 * @param {Object} integration
 * @returns {String} integrationConfig
 */
function _serializeInstanceConfig(integration) {
  //used for adobe experiences
  if (
    integration &&
    integration.instanceConfig &&
    integration.instanceConfig.filtersConfigJson &&
    typeof integration.instanceConfig.filtersConfigJson !== 'string'
  ) {
    integration.instanceConfig.filtersConfigJson = JSON.stringify(integration.instanceConfig.filtersConfigJson);
  }
  //we need to pass down either the full integration instance config text for the integration designer compatible experience or we parse the object of instanceConfig into a string for those that are not integration designer compatible as at the end of the process we need to submit a string value to the BE
  if (IntegrationEnums.designerIntegrationSupport.indexOf(integration.typeRef) === -1) {
    const instanceConfig = { ...integration.instanceConfig };
    // blueprint fields are not present for wizard integrations
    if (integration.blueprint.fields) {
      for (const field of integration.blueprint.fields) {
        const value = integration.instanceConfig[field.id];

        switch (field.type) {
          case IntegrationEnums.integrationFieldTypes.SCRIPT:
            const src = /src="(.+)"/i.exec(value);
            if (value && src) {
              instanceConfig[field.id] = src[1];
            }
            break;
        }
      }
    }

    return JSON.stringify(instanceConfig);
  } else if (IntegrationEnums.wizardIntegrationSupport.indexOf(integration.typeRef) !== -1) {
    // Wizard integrations should deal with instanceConfig (not instanceConfigText like other editor integrations)
    return JSON.stringify(integration.instanceConfig);
  } else {
    return integration.instanceConfigText;
  }
}

/**
 * Transforms based on field type: API -> App
 *
 * Certain fields can't be stored in the datastore in the form they need to be
 * presented to the user. Convert from the DATASTORE to APP form.
 *
 * @param {Object} integration
 * @returns {Object} integrationConfig
 */
function _deserializeInstanceConfig(integration) {
  const instanceConfig = _.isEmpty(integration.instanceConfig) ? {} : integration.instanceConfig;

  // blueprint fields are not present for wizard integrations (there is a step definition instead)
  if (integration.blueprint.fields) {
    for (const { id, type } of integration.blueprint.fields) {
      const value = instanceConfig[id];

      switch (type) {
        case IntegrationEnums.integrationFieldTypes.SCRIPT:
          if (value) {
            instanceConfig[id] = `<script src="${value}"></script>`;
          }
          break;
      }
    }
  }

  //This value [paramsType] can be easily messed up as it is a string set inside a complex JSON string and clients can manually work with it. We only support for it 2 enum stats , which are "all" and "mapped". In case the value is missing or wrongly set we assume it to be set to "mapped" as that is the value the BE defaults to.
  //We need to set this from inside the _deserializeInstanceConfig as it is a value deep inside the instanceConfig JSON object
  const paramsType = instanceConfig && instanceConfig.paramsType;
  instanceConfig.paramsType = _.includes(IntegrationEnums.integrationParamsTypes.ALL, paramsType)
    ? paramsType
    : IntegrationEnums.integrationParamsTypes.MAPPED;

  if (instanceConfig && instanceConfig.filtersConfigJson && typeof instanceConfig.filtersConfigJson === 'string')
    instanceConfig.filtersConfigJson = JSON.parse(instanceConfig.filtersConfigJson);

  return instanceConfig;
}

/**
 * An array of keys on the API Integration object that should be shared between the API requests and responses
 */
const _keys = [
  'blueprint',
  'classRef',
  'containerRef',
  'eventType',
  'fieldMapping',
  'mappingData',
  'help',
  'icon',
  'id',
  'instanceConfig',
  'instanceConfigText',
  'lastUpdated',
  'modifiedBy',
  'name',
  'status',
  'typeConfig',
  'typeRef',
];

export default {
  create,
  deserialize,
  serialize,
  fieldTypeMap,
};
